import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate, Link, useParams, useLocation } from "react-router-dom";
import { API_BASE_URL } from "../utils/config";
import {
    FiUser, FiMail, FiLock, FiPhone, FiMapPin,
    FiArrowRight, FiEye, FiEyeOff, FiBriefcase, FiLayers,
    FiX, FiAlertCircle, FiCheckCircle
} from "react-icons/fi";
import { FaTasks } from "react-icons/fa";

const CandidateRegister = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { jobId } = useParams();
    const verifiedEmail = location.state?.email || "";

    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        email: verifiedEmail,
        password: "",
        phone: "",
        address: ""
    });

    useEffect(() => {
        if (verifiedEmail) {
            setFormData(prev => ({ ...prev, email: verifiedEmail }));
        }
    }, [verifiedEmail]);

    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ text: "", type: "" });

    useEffect(() => {
        if (!verifiedEmail) {
            if (jobId) {
                navigate(`/email-verification/${jobId}`);
            } else {
                navigate("/email-verification");
            }
        }
    }, [verifiedEmail, navigate, jobId]);

    const [showPassword, setShowPassword] = useState(false);
    const [availableRoles, setAvailableRoles] = useState([]);
    const [selectedRoles, setSelectedRoles] = useState([]);
    const [showRoleDropdown, setShowRoleDropdown] = useState(false);
    const [roleSearch, setRoleSearch] = useState("");
    const roleDropdownRef = useRef(null);

    useEffect(() => {
        const fetchRoles = async () => {
            try {
                const res = await axios.get(`${API_BASE_URL}/roles/all`);
                let rolesData = [];
                if (Array.isArray(res.data)) rolesData = res.data;
                else if (Array.isArray(res.data.data)) rolesData = res.data.data;
                else if (Array.isArray(res.data.roles)) rolesData = res.data.roles;
                
                const uniqueRoles = rolesData.map((r, index) => {
                    if (typeof r === 'string') return r;
                    return r.name || r.title || r.role || "";
                }).filter(r => r !== "");
                
                setAvailableRoles([...new Set(uniqueRoles)]);
            } catch (err) {
                console.error("Failed to fetch roles:", err);
            }
        };
        fetchRoles();
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (roleDropdownRef.current && !roleDropdownRef.current.contains(event.target)) {
                setShowRoleDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        if (!showRoleDropdown) {
            setRoleSearch("");
        }
    }, [showRoleDropdown]);

    const handleRoleToggle = (role) => {
        setSelectedRoles(prev => 
            prev.includes(role) 
                ? prev.filter(r => r !== role) 
                : [...prev, role]
        );
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (selectedRoles.length === 0) {
            setMessage({ text: "Please select at least one role!", type: "error" });
            return;
        }

        setLoading(true);
        setMessage({ text: "", type: "" });

        const payload = {
            name: `${formData.firstName} ${formData.lastName}`,
            email: formData.email,
            phone: formData.phone,
            address: formData.address,
            password: formData.password,
            role: selectedRoles.join(", ")
        };

        try {
            const res = await axios.post(`${API_BASE_URL}/candidate/register`, payload);
            if (res.data.token) {
                const token = res.data.token;
                localStorage.setItem("candidateToken", token);

                // Fetch profile to get the definitive ID and Name
                try {
                    const profileRes = await axios.get(`${API_BASE_URL}/candidate/profile`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    localStorage.setItem("candidateId", profileRes.data._id);
                    localStorage.setItem("candidateName", profileRes.data.name);
                    localStorage.setItem("userRole", "candidate");
                } catch (profileErr) {
                    console.error("Profile fetch error after registration:", profileErr);
                    // Fallback to basic info if profile fetch fails
                    localStorage.setItem("candidateName", `${formData.firstName} ${formData.lastName}`);
                    localStorage.setItem("userRole", "candidate");
                }

                setMessage({ text: "Registration successful! Redirecting...", type: "success" });
                
                setTimeout(() => {
                    if (jobId) {
                        navigate(`/applying-job/${jobId}`);
                    } else {
                        navigate("/candidate-dashboard");
                    }
                }, 1500);
            }
        } catch (err) {
            console.error("Registration error:", err);
            setMessage({
                text: err.response?.data?.message || "Registration failed. Please try again.",
                type: "error"
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6 font-sans">
            <div className="w-full max-w-6xl bg-white rounded-[2.5rem] shadow-2xl shadow-indigo-100/50 overflow-hidden flex flex-col md:flex-row border border-slate-100 animate-in fade-in zoom-in duration-500">

                {/* Visual Side */}
                <div className="md:w-5/12 bg-gradient-to-br from-indigo-600 via-purple-600 to-blue-600 p-12 text-white flex flex-col justify-between relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at -20% -20%,rgba(255,255,255,0.2)_0%,transparent_60%)]"></div>
                    <div className="absolute bottom-[-10%] right-[-10%] w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>

                    <div className="relative z-10">
                        <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center mb-10 backdrop-blur-md border border-white/30 shadow-xl">
                            <FaTasks className="text-2xl" />
                        </div>
                        <h2 className="text-4xl font-black leading-tight mb-4">
                            Build your <br />
                            <span className="text-indigo-200">professional profile.</span>
                        </h2>
                        <p className="text-lg text-indigo-100/80 font-medium leading-relaxed">
                            Join our talent community and land your dream job at Timely Health.
                        </p>
                    </div>

                    <div className="relative z-10 space-y-6">
                        <div className="flex items-center gap-4 group">
                            <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center border border-white/20 group-hover:bg-white/20 transition-all">
                                <FiCheckCircle className="text-indigo-200" />
                            </div>
                            <span className="text-xs font-bold text-indigo-100 uppercase tracking-wider">Quick 1-minute setup</span>
                        </div>
                        <div className="flex items-center gap-4 group">
                            <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center border border-white/20 group-hover:bg-white/20 transition-all">
                                <FiCheckCircle className="text-indigo-200" />
                            </div>
                            <span className="text-xs font-bold text-indigo-100 uppercase tracking-wider">Apply with one click</span>
                        </div>
                    </div>
                </div>

                {/* Form Side */}
                <div className="md:w-7/12 p-12 md:p-16 flex flex-col justify-center overflow-y-auto max-h-[90vh] no-scrollbar bg-white">
                    <div className="mb-12">
                        <h1 className="text-2xl font-black text-slate-800 mb-1">Create Account</h1>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                            Join the elite talent pool
                        </p>
                    </div>

                    {message.text && (
                        <div className={`mb-8 p-4 rounded-xl flex items-center gap-3 animate-in slide-in-from-top-4 duration-300 border ${message.type === "success"
                            ? "bg-emerald-50 text-emerald-600 border-emerald-100"
                            : "bg-rose-50 text-rose-600 border-rose-100"
                            }`}>
                            {message.type === "success" ? <FiCheckCircle className="text-lg shrink-0" /> : <FiAlertCircle className="text-lg shrink-0" />}
                            <span className="text-sm font-bold">{message.text}</span>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">First Name</label>
                                <div className="relative group">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-300 group-focus-within:text-indigo-600 transition-colors">
                                        <FiUser className="text-lg" />
                                    </div>
                                    <input
                                        type="text"
                                        name="firstName"
                                        value={formData.firstName}
                                        onChange={handleChange}
                                        required
                                        className="w-full pl-11 pr-4 py-3.5 bg-white border border-slate-200 rounded-xl focus:ring-4 focus:ring-indigo-600/5 focus:border-indigo-600 outline-none transition-all text-sm font-medium text-slate-700 placeholder:text-slate-300 shadow-sm shadow-slate-100/50"
                                        placeholder="Alex"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Last Name</label>
                                <div className="relative group">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-300 group-focus-within:text-indigo-600 transition-colors">
                                        <FiUser className="text-lg" />
                                    </div>
                                    <input
                                        type="text"
                                        name="lastName"
                                        value={formData.lastName}
                                        onChange={handleChange}
                                        required
                                        className="w-full pl-11 pr-4 py-3.5 bg-white border border-slate-200 rounded-xl focus:ring-4 focus:ring-indigo-600/5 focus:border-indigo-600 outline-none transition-all text-sm font-medium text-slate-700 placeholder:text-slate-300 shadow-sm shadow-slate-100/50"
                                        placeholder="Johnson"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Email</label>
                                <div className="relative group">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-300 group-focus-within:text-indigo-600 transition-colors">
                                        <FiMail className="text-lg" />
                                    </div>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        readOnly
                                        required
                                        className="w-full pl-11 pr-4 py-3.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-4 focus:ring-indigo-600/5 focus:border-indigo-600 outline-none transition-all text-sm font-medium text-slate-400 placeholder:text-slate-300 shadow-sm shadow-slate-100/50 cursor-not-allowed"
                                        placeholder="alex@example.com"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Create Password</label>
                                <div className="relative group">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-300 group-focus-within:text-indigo-600 transition-colors">
                                        <FiLock className="text-lg" />
                                    </div>
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        name="password"
                                        value={formData.password}
                                        onChange={handleChange}
                                        required
                                        className="w-full pl-11 pr-12 py-3.5 bg-white border border-slate-200 rounded-xl focus:ring-4 focus:ring-indigo-600/5 focus:border-indigo-600 outline-none transition-all text-sm font-medium text-slate-700 placeholder:text-slate-300 shadow-sm shadow-slate-100/50"
                                        placeholder="Create your password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-indigo-600 transition-colors"
                                    >
                                        {showPassword ? <FiEyeOff className="text-lg" /> : <FiEye className="text-lg" />}
                                    </button>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Mobile</label>
                                <div className="relative group">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-300 group-focus-within:text-indigo-600 transition-colors">
                                        <FiPhone className="text-lg" />
                                    </div>
                                    <input
                                        type="text"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        required
                                        className="w-full pl-11 pr-4 py-3.5 bg-white border border-slate-200 rounded-xl focus:ring-4 focus:ring-indigo-600/5 focus:border-indigo-600 outline-none transition-all text-sm font-medium text-slate-700 placeholder:text-slate-300 shadow-sm shadow-slate-100/50"
                                        placeholder="+91 XXXXX XXXXX"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Location / Address</label>
                                <div className="relative group">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-300 group-focus-within:text-indigo-600 transition-colors">
                                        <FiMapPin className="text-lg" />
                                    </div>
                                    <input
                                        type="text"
                                        name="address"
                                        value={formData.address}
                                        onChange={handleChange}
                                        required
                                        className="w-full pl-11 pr-4 py-3.5 bg-white border border-slate-200 rounded-xl focus:ring-4 focus:ring-indigo-600/5 focus:border-indigo-600 outline-none transition-all text-sm font-medium text-slate-700 placeholder:text-slate-300 shadow-sm shadow-slate-100/50"
                                        placeholder="City, State"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2 col-span-1 md:col-span-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Desired Roles (Select Multiple)</label>
                                <div className="relative" ref={roleDropdownRef}>
                                    <div 
                                        onClick={() => setShowRoleDropdown(!showRoleDropdown)}
                                        className="w-full min-h-[50px] pl-4 pr-10 py-2.5 bg-white border border-slate-200 rounded-xl focus-within:ring-4 focus-within:ring-indigo-600/5 focus-within:border-indigo-600 outline-none transition-all text-sm font-medium text-slate-700 shadow-sm shadow-slate-100/50 flex flex-wrap gap-2 items-center cursor-pointer"
                                    >
                                        {selectedRoles.length === 0 ? (
                                            <span className="text-slate-300">Select roles...</span>
                                        ) : (
                                            selectedRoles.map((role) => (
                                                <span 
                                                    key={role} 
                                                    className="bg-indigo-50 text-indigo-600 px-3 py-1 rounded-lg text-xs font-bold flex items-center gap-1.5"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleRoleToggle(role);
                                                    }}
                                                >
                                                    {role}
                                                    <FiX className="hover:text-indigo-800 cursor-pointer" />
                                                </span>
                                            ))
                                        )}
                                    </div>
                                    <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-300">
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="m6 9 6 6 6-6" />
                                        </svg>
                                    </div>

                                    {showRoleDropdown && (
                                        <div className="absolute z-20 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-60 overflow-y-auto no-scrollbar p-2 space-y-1">
                                            <div className="px-1 py-1 sticky top-0 bg-white border-b border-slate-100 mb-2 z-10">
                                                <input 
                                                    type="text"
                                                    value={roleSearch}
                                                    onChange={(e) => setRoleSearch(e.target.value)}
                                                    placeholder="Search roles..."
                                                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs outline-none focus:border-indigo-600 font-medium"
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            </div>
                                            {availableRoles.filter(role => role.toLowerCase().includes(roleSearch.toLowerCase())).length === 0 ? (
                                                <div className="p-3 text-xs text-slate-400 text-center font-bold uppercase">No matching roles</div>
                                            ) : (
                                                availableRoles.filter(role => role.toLowerCase().includes(roleSearch.toLowerCase())).map((role) => {
                                                    const isSelected = selectedRoles.includes(role);
                                                    return (
                                                        <div
                                                            key={role}
                                                            onClick={() => handleRoleToggle(role)}
                                                            className={`px-3 py-2 text-xs font-bold rounded-lg cursor-pointer flex items-center justify-between transition-colors ${
                                                                isSelected 
                                                                    ? "bg-indigo-50 text-indigo-600" 
                                                                    : "text-slate-600 hover:bg-slate-50"
                                                            }`}
                                                        >
                                                            <span>{role}</span>
                                                            {isSelected && <FiCheckCircle className="text-indigo-600" />}
                                                        </div>
                                                    );
                                                })
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full py-6 bg-indigo-600 text-white font-black rounded-xl shadow-xl shadow-indigo-100 hover:bg-indigo-700 hover:-translate-y-0.5 active:translate-y-0 transition-all flex items-center justify-center gap-3 disabled:opacity-70 disabled:pointer-events-none group mt-6 uppercase tracking-widest text-sm"
                        >
                            {loading ? (
                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                            ) : (
                                <>
                                    <span>Create Account</span>
                                    <FiArrowRight className="text-sm group-hover:translate-x-1 transition-transform" />
                                </>
                            )}
                        </button>
                    </form>

                    <p className="mt-10 text-center text-[11px] text-slate-400 font-bold uppercase tracking-wider">
                        Already have an account?{" "}
                        <Link to={jobId ? `/candidate-login/${jobId}` : "/candidate-login"} className="text-indigo-600 font-black hover:underline underline-offset-8 transition-all">
                            Log In
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default CandidateRegister;
